import { useDataModel } from '@thd-nucleus/data-sources';
import { OOS_RECOMMENDATIONS_LIMIT } from '../../../constants/drawerFlows';
import { useOutOfStockPageLoadEvent } from './useOutOfStockPageLoadEvent';

const RECS = {
  apiName: 'more_like_this',
  key: 'major-appliances',
  dataSource: 'catalog',
  configOverrides: [
    { configName: 'includeItemDetails', configValue: 'true' },
    { configName: 'includePricing', configValue: 'true' }
  ]
};

const MAX_CANDIDATES = 20;

const DELIVERY_AVAILABLE = 'AVAILABLE';

export const getCandidateItemIds = (recsData, anchorItemId) => (recsData?.recs?.products || [])
  .filter((entry) => entry?.product?.itemId && !entry.isAnchor && entry.product.itemId !== anchorItemId)
  .map((entry) => entry.product.itemId)
  .filter((itemId, index, itemIds) => itemIds.indexOf(itemId) === index)
  .slice(0, MAX_CANDIDATES);

export const getDeliverableItemIds = (availabilityData) => {
  const services = availabilityData?.checkAvailability?.fulfillmentOptions?.[0]?.services;
  return (services?.[0]?.deliveryAvailabilityDetail || [])
    .filter((detail) => detail?.status === DELIVERY_AVAILABLE)
    .map((detail) => detail.itemId);
};

export const useOutOfStockRecommendations = ({
  itemId, storeId, zipCode, channel, skip, trackPageLoad = false
}) => {
  useOutOfStockPageLoadEvent(trackPageLoad && !skip);

  const recsResponse = useDataModel('outOfStockRecs', {
    ssr: false,
    skip,
    variables: {
      anchorId: itemId,
      apiName: RECS.apiName,
      key: RECS.key,
      configOverrides: RECS.configOverrides,
      dataSource: RECS.dataSource,
      appId: channel === 'desktop' ? 'desktop' : 'mobile',
      storeId
    }
  });

  const candidateItemIds = getCandidateItemIds(recsResponse.data, itemId);
  const skipAvailability = skip || !candidateItemIds.length || !zipCode || !storeId;
  const availabilityResponse = useDataModel('outOfStockRecsAvailability', {
    ssr: false,
    skip: skipAvailability,
    variables: {
      itemIds: candidateItemIds,
      zipCode: zipCode || '',
      localStoreId: storeId || ''
    }
  });

  const deliverableItemIds = getDeliverableItemIds(availabilityResponse.data);
  const recommendedItemIds = candidateItemIds
    .filter((candidateItemId) => deliverableItemIds.includes(candidateItemId))
    .slice(0, OOS_RECOMMENDATIONS_LIMIT);
  const skipProducts = skip || !recommendedItemIds.length;
  const productsResponse = useDataModel('outOfStockRecsProducts', {
    ssr: false,
    skip: skipProducts,
    variables: {
      itemIds: recommendedItemIds,
      storeId
    }
  });

  const currentItemResponse = useDataModel('product', {
    ssr: false,
    skip,
    variables: {
      itemId,
      storeId
    }
  });

  const currentItemSpecificationsResponse = useDataModel('outOfStockCurrentItemSpecifications', {
    ssr: false,
    skip,
    variables: {
      itemId
    }
  });

  const loading = recsResponse.loading
    || availabilityResponse.loading
    || productsResponse.loading
    || currentItemResponse.loading;

  const products = recommendedItemIds
    .map((recommendedItemId) => productsResponse.data?.products?.find(
      (product) => product?.itemId === recommendedItemId
    ))
    .filter(Boolean);

  return {
    products,
    loading,
    currentItemSpecificationGroup: currentItemSpecificationsResponse.data?.product?.specificationGroup || null
  };
};
